import { useEffect, useState } from "react";
import CustomButton from "../../components/CustomButton";
import CustomInput from "../../components/CustomInput";
import CustomSelect from "../../components/CustomSelect";
import PageHeader from "../../components/PageHeader";
import SubHeadings from "../../components/SubHeadings";
import { AddStudentForm } from "../../types/studentType";
import StudentApis from "../../api/studentApis";
import { toast } from "sonner";
import { useDispatch } from "react-redux";
import { AppDispatch, RootState } from "../../store";
import { useSelector } from "react-redux";
import { removeNonNumeric } from "../../util/functions/UniversalFunctions";
import { getGroups } from "../../features/group-houses/GroupSlice";
import AcademicCycleSelect from "../../components/common-input/AcademicCycle";
import { fetchClasses } from "../../features/classes/ClassesSlice";

export default function AddStudents() {
  const initialFormState: AddStudentForm = {
    admissionNumber: "",
    fullName: "",
    gender: "",
    dateOfBirth: "",
    admissionYear: "",
    classSection: "",
    academicCycle: "",
    motherFullName: "",
    motherPhoneNumber: "",
    motherEmail: "",
    fatherFullName: "",
    fatherPhoneNumber: "",
    fatherEmail: "",
    branchCode: "",
    classId: "",
    groupId: "",
    academicCycleId: "",
    studentCategory: "",
  };

  const [formData, setFormData] = useState<AddStudentForm>(initialFormState);

  const dispatch = useDispatch<AppDispatch>();

  const { schoolBranch } = useSelector((state: RootState) => state.access);
  const { classes, page, size, filters } = useSelector(
    (state: RootState) => state.classes,
  );

  const branchCode = schoolBranch?.schoolBranchCode;

  const { groups, isLoading, pagination } = useSelector(
    (state: RootState) => state.group,
  );

  useEffect(() => {
    dispatch(
      getGroups({
        page: pagination.currentPage,
        size: pagination.pageSize,
        branchCode,
      }),
    );
  }, [dispatch, pagination.currentPage, pagination.pageSize, branchCode]);

  const { academicCycle } = formData;

  useEffect(() => {
    if (!branchCode) return;

    dispatch(
      fetchClasses({
        branchCode,
        page: 0,
        size: 100,
        filters: {
          academicCycle,
        },
      }),
    );
  }, [dispatch, branchCode, academicCycle]);

  const handleInputChange = (
    event: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    const { name, value, dataset } = event.target;

    if (name === "academicCycleId") {
      setFormData((prev) => ({
        ...prev,
        academicCycleId: Number(value), // for POST
        academicCycle: dataset?.label || "", // for GET
      }));
      return;
    }

    // phone number fields
    if (name === "motherPhoneNumber" || name === "fatherPhoneNumber") {
      const numericValue = removeNonNumeric(value).slice(0, 10); // max 10 digits

      setFormData((prev) => ({
        ...prev,
        [name]: numericValue,
      }));
      return;
    }

    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const validateform =
    formData.fullName &&
    formData.dateOfBirth &&
    formData.admissionYear &&
    formData.gender &&
    // formData.classSection &&
    formData.academicCycleId;

  const validateParentInfo = () => {
    const hasMother = formData.motherFullName && formData.motherPhoneNumber;
    // || formData.motherEmail;
    const hasFather = formData.fatherFullName && formData.fatherPhoneNumber;
    // || formData.fatherEmail;

    return hasMother || hasFather;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!branchCode) return;

    if (!validateParentInfo()) {
      toast.error("Any one parent detail (Name and mobile No) is mandatory");
      return;
    }

    try {
      await StudentApis.addStudent({ ...formData, branchCode });
      toast.success("Student added successfully!");
      setFormData(initialFormState);
    } catch (err) {
      toast.error("Failed to add student");
    }
  };

  return (
    <div className="flex flex-col gap-5">
      <div className="flex justify-between">
        <PageHeader showBack />
      </div>

      <form className="grid gap-5" onSubmit={handleSubmit}>
        <div className="bg-white px-6 py-5 rounded-lg flex flex-col gap-6">
          <SubHeadings text="Student Details" />
          <div className="grid gap-7">
            <div className="grid lg:grid-cols-2 gap-7">
              <CustomInput
                name="admissionNumber"
                label="Admission Number: (Leave empty for autogeneration)"
                type="text"
                readOnly
                placeholder="To be auto-generated"
                value={formData.admissionNumber?.toString()}
                onChange={handleInputChange}
                inputClassName="w-full"
              />
              <CustomInput
                name="fullName"
                label="Name"
                type="text"
                placeholder="Enter the student's name"
                value={formData.fullName}
                onChange={handleInputChange}
                inputClassName="w-full"
                required
              />
            </div>

            <div className="flex gap-7">
              <label className="text-sm font-semibold text-neutral-400 block">
                Gender
                <span className="text-error-500">*</span>
              </label>
              <div className="flex items-center gap-4">
                <label className="flex items-center gap-2">
                  <CustomInput
                    name="gender"
                    type="radio"
                    value="MALE"
                    checked={formData?.gender === "MALE"}
                    onChange={handleInputChange}
                    inputClassName="w-3 h-3"
                    required
                  />
                  <span
                    className={`text-sm font-semibold ${
                      formData?.gender === "MALE"
                        ? "text-primary-500"
                        : "text-neutral-300"
                    }`}
                  >
                    Male
                  </span>
                </label>

                <label className="flex items-center gap-2">
                  <CustomInput
                    name="gender"
                    type="radio"
                    value="FEMALE"
                    checked={formData?.gender === "FEMALE"}
                    onChange={handleInputChange}
                    inputClassName="w-3 h-3"
                  />
                  <span
                    className={`text-sm font-semibold ${
                      formData.gender === "FEMALE"
                        ? "text-primary-500"
                        : "text-neutral-300"
                    }`}
                  >
                    Female
                  </span>
                </label>
              </div>
            </div>

            <div className="grid lg:grid-cols-3 gap-7">
              <CustomInput
                name="dateOfBirth"
                label="Date of Birth"
                type="date"
                placeholder="DD/MM/YYYY"
                value={formData.dateOfBirth}
                onChange={handleInputChange}
                inputClassName="w-full"
                required
              />
              <CustomSelect
                name="admissionYear"
                label="Admission Year"
                value={formData.admissionYear}
                options={[
                  { label: "2026–27", value: 2026 },
                  { label: "2027–28", value: 2027 },
                  { label: "2028–29", value: 2028 },
                ]}
                placeholder="Select admission year"
                onChange={handleInputChange}
                required
              />
              <AcademicCycleSelect
                name="academicCycleId"
                label="Academic Cycle"
                value={formData.academicCycleId}
                placeholder="Select academic cycle"
                onChange={handleInputChange}
                required
              />
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-7">
              <CustomSelect
                key={formData.academicCycleId}
                name="classId"
                label="Class/Section"
                placeholder={"Select class/Section"}
                value={formData.classId}
                onChange={handleInputChange}
                selectClassName="w-full"
                options={
                  classes?.length > 0
                    ? classes.map((c) => ({
                        label: c.className,
                        value: c.classId,
                      }))
                    : [
                        {
                          label: "No classes available",
                          value: "",
                        },
                      ]
                }

                // readOnly={!formData.academicCycleId}
              />

              <CustomSelect
                name="studentCategory"
                label="Student Category"
                value={formData.studentCategory}
                options={[
                  { label: "General", value: "GENERAL" },
                  { label: "OBC", value: "OBC" },
                  { label: "SC", value: "SC" },
                  { label: "ST", value: "ST" },
                ]}
                placeholder="Select student category"
                onChange={handleInputChange}
                required
              />
              <CustomSelect
                name="groupId"
                label="Group/House Name"
                value={formData.groupId}
                options={groups?.map((item) => ({
                  label: item.groupName,
                  value: item.groupId,
                }))}
                placeholder="Select group/house"
                onChange={handleInputChange}
              />
            </div>
          </div>
        </div>

        <div className="bg-white px-6 py-5 rounded-lg flex flex-col gap-6">
          <div className="flex justify-between items-center w-full">
            <SubHeadings text="Parent Details" />
            <span className="text-xs text-error-500 font-medium">
              *Any one parent detail is mandatory
            </span>
          </div>

          <div className="grid lg:grid-cols-2 gap-7">
            <CustomInput
              name="motherFullName"
              label="Mother's Name"
              type="text"
              placeholder="Enter mother's name"
              value={formData.motherFullName}
              onChange={handleInputChange}
              inputClassName="w-full"
            />
            <CustomInput
              name="motherPhoneNumber"
              label="Mother's Phone Number"
              type="tel"
              placeholder="Enter mother's phone number"
              value={formData.motherPhoneNumber}
              onChange={handleInputChange}
              inputClassName="w-full"
            />
            <CustomInput
              name="motherEmail"
              label="Mother's Email"
              type="email"
              placeholder="Enter mother's email"
              value={formData.motherEmail}
              onChange={handleInputChange}
              inputClassName="w-full"
            />
            <CustomInput
              name="fatherFullName"
              label="Father's Name"
              type="text"
              placeholder="Enter father's name"
              value={formData.fatherFullName}
              onChange={handleInputChange}
              inputClassName="w-full"
            />
            <CustomInput
              name="fatherPhoneNumber"
              label="Father's Phone Number"
              type="tel"
              placeholder="Enter father's phone number"
              value={formData.fatherPhoneNumber}
              onChange={handleInputChange}
              inputClassName="w-full"
            />
            <CustomInput
              name="fatherEmail"
              label="Father's Email"
              type="email"
              placeholder="Enter father's email"
              value={formData.fatherEmail}
              onChange={handleInputChange}
              inputClassName="w-full"
            />
          </div>
        </div>

        <div className="flex justify-end items-end mx-6">
          <CustomButton
            color="bg-primary-500"
            text="Submit"
            type="submit"
            disabled={!validateform || !validateParentInfo}
          />
        </div>
      </form>
    </div>
  );
}
